import { Modal } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { IconCamera, IconFileUpload, IconLink } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalOptionButton } from 'src/Components/Buttons/ModalOptionButton';
import { UploadSource } from 'src/Components/Inputs/UploadFileButton/UploadFileButton.types';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';

import DirectorySelectionModal from '../DirectorySelectionModal/DirectorySelectionModal.component';

import styles from './UploadFileModal.module.scss';
import { Props } from './UploadFileModal.types';

const UploadFileModal = (props: Props) => {
  const {
    opened,
    onClose,
    onDrop,
    single,
    modalTitle,
    cameraLabel,
    cameraSubtitle,
    fileUploadLabel,
    fileUploadSubtitle,
    existingFileLabel,
    existingFileSubtitle,
    onSourceSelected,
  } = props;
  const { t } = useTranslation();
  const [directorySelectionModalOpened, setDirectorySelectionModalOpened] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isMobile = useIsMobile();

  const notifySource = (source: UploadSource) => {
    onSourceSelected?.(source);
  };

  const handleFilesPicked = (event: React.ChangeEvent<HTMLInputElement>, source: UploadSource) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) {
      event.target.value = '';
      return;
    }
    notifySource(source);
    const files = Array.from(fileList) as FileWithPath[];
    onDrop(single ? [files[0]] : files);
    event.target.value = '';
  };

  const handleExistingFileSelect = (fileId: string) => {
    if (single && props.onExistingFileSelect) {
      notifySource('existing');
      props.onExistingFileSelect(fileId);
    }
    setDirectorySelectionModalOpened(false);
  };

  const handleExistingFilesSelect = (fileIds: string[]) => {
    if (!single && props.onExistingFilesSelect) {
      notifySource('existing');
      props.onExistingFilesSelect(fileIds);
    }
    setDirectorySelectionModalOpened(false);
  };

  const resolvedTitle =
    modalTitle ??
    t(
      single ? 'fileUploadModal.title' : 'fileUploadModal.titlePlural',
      single ? 'Ajouter un document' : 'Ajouter des documents'
    );

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        withCloseButton={true}
        title={resolvedTitle}
        size="md"
      >
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple={!single}
          style={{ display: 'none' }}
          onChange={e => handleFilesPicked(e, 'camera')}
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple={!single}
          style={{ display: 'none' }}
          onChange={e => handleFilesPicked(e, 'upload')}
        />
        <div className={styles.container}>
          {isMobile && (
            <ModalOptionButton
              icon={<IconCamera size={32} />}
              title={cameraLabel ?? t('fileUploadModal.option.camera', 'Prendre en photo')}
              description={
                cameraSubtitle ??
                t('fileUploadModal.option.cameraSubtitle', "Avec l'appareil photo")
              }
              onClick={() => cameraInputRef.current?.click()}
            />
          )}
          <ModalOptionButton
            icon={<IconFileUpload size={32} />}
            title={fileUploadLabel ?? t('fileUploadModal.option.upload', 'Importer un fichier')}
            description={
              fileUploadSubtitle ??
              t('fileUploadModal.option.uploadSubtitle', 'Depuis cet appareil')
            }
            onClick={() => fileInputRef.current?.click()}
          />
          <ModalOptionButton
            icon={<IconLink size={32} />}
            title={
              existingFileLabel ?? t('fileUploadModal.option.existing', 'Lier un document existant')
            }
            description={
              existingFileSubtitle ??
              t('fileUploadModal.option.existingSubtitle', 'Depuis vos documents déjà ajoutés')
            }
            onClick={() => setDirectorySelectionModalOpened(true)}
          />
        </div>
      </Modal>
      {single ? (
        <DirectorySelectionModal
          title={t('fileUploadModal.linkFile', 'Lier un document')}
          subtitle={t('fileUploadModal.chooseFile', 'Sélectionnez un document à lier')}
          onValidate={file => {
            if (file.id) {
              handleExistingFileSelect(file.id);
            }
          }}
          buttonText={t('fileUploadModal.link', 'Lier ce document')}
          open={directorySelectionModalOpened}
          onClose={() => setDirectorySelectionModalOpened(false)}
          shouldSelectFile
        />
      ) : (
        <DirectorySelectionModal
          title={t('fileUploadModal.linkFiles', 'Lier des documents')}
          subtitle={t('fileUploadModal.chooseFiles', 'Sélectionnez les documents à lier')}
          onValidate={files => {
            const fileIds = files.map(f => f.id).filter((id): id is string => !!id);
            if (fileIds.length > 0) {
              handleExistingFilesSelect(fileIds);
            }
          }}
          buttonText={t('fileUploadModal.linkSelected', 'Lier les documents sélectionnés')}
          open={directorySelectionModalOpened}
          onClose={() => setDirectorySelectionModalOpened(false)}
          shouldSelectFile
          multiple
        />
      )}
    </>
  );
};

export default UploadFileModal;
